import React from "react";
import toast from "react-hot-toast";

// How long the in-app incoming-call toast stays on screen (a ringing call
// window) before auto-dismissing so toasts don't stack across missed calls.
export const INCOMING_CALL_TOAST_DURATION_MS = 45000;

// Vetic brand palette (matches the app's primary navy `#193A6A` and slate text).
const VETIC_NAVY = "#193A6A";
const VETIC_NAVY_DARK = "#122C50";
const VETIC_ACCENT = "#4D9FFF";
const VETIC_TEXT = "#263238";
const VETIC_MUTED = "#6C727F";
const VETIC_BORDER = "#E0E0E0";
const CHROME_BLUE = "#1A73E8";

// One full loop of the how-to animation, in ms. Two scenes play back to back:
// scene 1 (left ⓘ → Pop-ups toggle) and scene 2 (right blocked icon → Always
// allow → Done), so it needs to be long enough to read both.
const GUIDE_LOOP = "10s";

// A single Chrome-style toggle switch (track + sliding knob). `on` renders the
// enabled (blue) state; `animated` drives an animated off→on flip via the named
// keyframes below.
const GuideToggle: React.FC<{ on?: boolean; animated?: boolean }> = ({ on, animated }) => (
  <span
    style={{
      position: "relative",
      width: 30,
      height: 16,
      borderRadius: 8,
      flexShrink: 0,
      background: on ? CHROME_BLUE : "#C4C9D2",
      animation: animated ? `veticGuideToggleTrack ${GUIDE_LOOP} ease-in-out infinite` : undefined,
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 2,
        left: on ? 16 : 2,
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: "#FFFFFF",
        boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
        animation: animated ? `veticGuideToggleKnob ${GUIDE_LOOP} ease-in-out infinite` : undefined,
      }}
    />
  </span>
);

// Inline highlight chip for the key terms inside the step list, so the words a
// user must look for on screen stand out (navy by default, green for "Allow").
const Hl: React.FC<{ children: React.ReactNode; tone?: "navy" | "green" }> = ({
  children,
  tone = "navy",
}) => (
  <span
    style={{
      fontWeight: 700,
      color: tone === "green" ? "#1E7C46" : VETIC_NAVY,
      background: tone === "green" ? "rgba(30,124,70,0.10)" : "rgba(25,58,106,0.08)",
      borderRadius: 4,
      padding: "1px 5px",
    }}
  >
    {children}
  </span>
);

// A GIF-like, self-contained animation walking a desktop-Chrome user through the
// two ways to enable pop-ups, mirroring Chrome's real UI:
//   Scene 1 (first half):  click the "ⓘ View site information" icon at the LEFT
//     of the address bar → the site panel drops down → the "Pop-ups and
//     redirects" toggle flips off→on.
//   Scene 2 (second half): click the pop-ups-blocked icon at the RIGHT of the
//     address bar → the bubble opens → "Always allow pop-ups and redirects" is
//     selected → Done.
// Built with pure CSS keyframes so it needs no binary asset and works offline.
//
// To use a real screen-recording GIF instead, drop the file in assets and
// replace <ChromePopupGuide /> below with:
//   <img src={popupGuideGif} alt="How to allow pop-ups in Chrome" style={{ width: "100%", borderRadius: 10 }} />
const ChromePopupGuide: React.FC = () => {
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: 244,
        borderRadius: 10,
        border: `1px solid ${VETIC_BORDER}`,
        background: "#F5F7FA",
        overflow: "hidden",
      }}
    >
      <style>
        {`
          @keyframes veticGuideCursor {
            0%   { top:120px; left:120px; opacity:0; transform:scale(1); }
            3%   { top:120px; left:120px; opacity:1; }
            9%   { top:6px;   left:6px;   transform:scale(1); }    /* scene1: left ⓘ */
            11%  { top:6px;   left:6px;   transform:scale(0.8); }  /* click */
            13%  { top:6px;   left:6px;   transform:scale(1); }
            26%  { top:130px; left:205px; transform:scale(1); }    /* pop-ups toggle */
            29%  { top:130px; left:205px; transform:scale(0.8); }  /* click */
            31%  { top:130px; left:205px; transform:scale(1); }
            44%  { top:130px; left:205px; transform:scale(1); }    /* hold */
            52%  { top:8px;   left:292px; transform:scale(1); }    /* scene2: right blocked icon */
            55%  { top:8px;   left:292px; transform:scale(0.8); }  /* click */
            57%  { top:8px;   left:292px; transform:scale(1); }
            69%  { top:66px;  left:112px; transform:scale(1); }    /* Always allow radio */
            72%  { top:66px;  left:112px; transform:scale(0.8); }  /* click */
            74%  { top:66px;  left:112px; transform:scale(1); }
            83%  { top:120px; left:262px; transform:scale(1); }    /* Done */
            86%  { top:120px; left:262px; transform:scale(0.8); }  /* click */
            88%  { top:120px; left:262px; transform:scale(1); }
            96%  { top:120px; left:262px; opacity:1; }
            100% { top:120px; left:262px; opacity:0; }
          }
          @keyframes veticGuideInfoPulse {
            0%, 5%   { box-shadow:0 0 0 0 rgba(25,58,106,0); }
            10%      { box-shadow:0 0 0 5px rgba(25,58,106,0.25); }
            16%,100% { box-shadow:0 0 0 0 rgba(25,58,106,0); }
          }
          @keyframes veticGuideBlockedPulse {
            0%, 2%   { box-shadow:0 0 0 0 rgba(237,1,1,0.55); }
            9%       { box-shadow:0 0 0 7px rgba(237,1,1,0); }
            10%,49%  { box-shadow:0 0 0 0 rgba(237,1,1,0); }
            51%      { box-shadow:0 0 0 0 rgba(237,1,1,0.55); }
            58%      { box-shadow:0 0 0 7px rgba(237,1,1,0); }
            60%,100% { box-shadow:0 0 0 0 rgba(237,1,1,0); }
          }
          @keyframes veticGuidePanelLeft {
            0%, 11%  { opacity:0; transform:translateY(-6px); }
            15%,44%  { opacity:1; transform:translateY(0); }
            48%,100% { opacity:0; transform:translateY(-6px); }
          }
          @keyframes veticGuideBubbleRight {
            0%, 55%  { opacity:0; transform:translateY(-6px); }
            60%,94%  { opacity:1; transform:translateY(0); }
            98%,100% { opacity:0; transform:translateY(-6px); }
          }
          @keyframes veticGuideRowHi {
            0%, 29%  { background:transparent; }
            33%,46%  { background:rgba(26,115,232,0.10); }
            48%,100% { background:transparent; }
          }
          @keyframes veticGuideToggleTrack {
            0%, 29%  { background:#C4C9D2; }
            33%,100% { background:${CHROME_BLUE}; }
          }
          @keyframes veticGuideToggleKnob {
            0%, 29%  { left:2px; }
            33%,100% { left:16px; }
          }
          @keyframes veticGuideRadio {
            0%, 71%  { background:#FFFFFF; border-color:#B0B7C3; }
            75%,100% { background:${CHROME_BLUE}; border-color:${CHROME_BLUE}; box-shadow: inset 0 0 0 2px #FFFFFF; }
          }
          @keyframes veticGuideDone {
            0%, 84%  { background:transparent; }
            88%,100% { background:rgba(26,115,232,0.12); }
          }
        `}
      </style>

      {/* Chrome address bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 30,
          padding: "0 8px",
          background: "#FFFFFF",
          borderBottom: `1px solid ${VETIC_BORDER}`,
        }}
      >
        {/* ⓘ View site information (LEFT of the omnibox) */}
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 16,
            height: 16,
            flexShrink: 0,
            borderRadius: "50%",
            border: `1.5px solid ${VETIC_MUTED}`,
            color: VETIC_MUTED,
            fontSize: 10,
            fontWeight: 700,
            fontStyle: "italic",
            fontFamily: "Georgia, serif",
            animation: `veticGuideInfoPulse ${GUIDE_LOOP} ease-out infinite`,
          }}
        >
          i
        </span>
        <div style={{ flex: 1, fontSize: 9, color: VETIC_TEXT, whiteSpace: "nowrap", overflow: "hidden" }}>
          https://vms.vetic.in/app/customer/profile/search
        </div>
        {/* Pop-ups-blocked icon (RIGHT of the omnibox) */}
        <span
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 18,
            flexShrink: 0,
            borderRadius: 4,
            animation: `veticGuideBlockedPulse ${GUIDE_LOOP} ease-out infinite`,
          }}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={VETIC_TEXT} strokeWidth="2">
            <rect x="3" y="6" width="12" height="12" rx="1.5" />
            <path d="M14 4 L20 4 L20 10 M20 4 L13 11" />
          </svg>
          <span
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#ED0101",
            }}
          />
        </span>
        {/* ☆ Bookmark star */}
        <span style={{ fontSize: 12, color: VETIC_MUTED, flexShrink: 0 }}>☆</span>
      </div>

      {/* SCENE 1 — Chrome "site information" panel (opened from the ⓘ icon) */}
      <div
        style={{
          position: "absolute",
          top: 36,
          left: 8,
          width: 232,
          background: "#FFFFFF",
          borderRadius: 8,
          border: `1px solid ${VETIC_BORDER}`,
          boxShadow: "0 8px 22px rgba(25,58,106,0.20)",
          padding: "10px 12px",
          animation: `veticGuidePanelLeft ${GUIDE_LOOP} ease-in-out infinite`,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: VETIC_TEXT, marginBottom: 8 }}>
          vms.vetic.in
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px" }}>
          <span style={{ fontSize: 12 }}>📍</span>
          <span style={{ flex: 1, fontSize: 10, color: VETIC_TEXT }}>Location</span>
          <GuideToggle on />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px" }}>
          <span style={{ fontSize: 12 }}>🎙️</span>
          <span style={{ flex: 1, fontSize: 10, color: VETIC_TEXT }}>Microphone</span>
          <GuideToggle on />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 4px",
            borderRadius: 6,
            animation: `veticGuideRowHi ${GUIDE_LOOP} ease-in-out infinite`,
          }}
        >
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke={VETIC_TEXT} strokeWidth="2">
            <rect x="3" y="6" width="12" height="12" rx="1.5" />
            <path d="M14 4 L20 4 L20 10 M20 4 L13 11" />
          </svg>
          <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: VETIC_TEXT }}>
            Pop-ups and redirects
          </span>
          <GuideToggle animated />
        </div>
        <div style={{ height: 1, background: VETIC_BORDER, margin: "6px 0" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px" }}>
          <span style={{ fontSize: 12 }}>⚙️</span>
          <span style={{ flex: 1, fontSize: 10, color: VETIC_TEXT }}>Site settings</span>
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke={VETIC_MUTED} strokeWidth="2">
            <path d="M14 4 L20 4 L20 10 M20 4 L12 12" />
            <path d="M18 14 L18 19 L5 19 L5 6 L10 6" />
          </svg>
        </div>
      </div>

      {/* SCENE 2 — "Pop-ups blocked" bubble (opened from the right blocked icon) */}
      <div
        style={{
          position: "absolute",
          top: 36,
          right: 8,
          width: 214,
          background: "#FFFFFF",
          borderRadius: 8,
          border: `1px solid ${VETIC_BORDER}`,
          boxShadow: "0 8px 22px rgba(25,58,106,0.20)",
          padding: "10px 12px",
          animation: `veticGuideBubbleRight ${GUIDE_LOOP} ease-in-out infinite`,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: VETIC_TEXT, marginBottom: 8 }}>
          Pop-ups blocked
        </div>
        {/* Always allow (selected) */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 2px" }}>
          <span
            style={{
              width: 12,
              height: 12,
              flexShrink: 0,
              borderRadius: "50%",
              border: "2px solid #B0B7C3",
              background: "#FFFFFF",
              animation: `veticGuideRadio ${GUIDE_LOOP} ease-in-out infinite`,
            }}
          />
          <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: VETIC_TEXT }}>
            Always allow pop-ups and redirects
          </span>
        </div>
        {/* Continue blocking */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 2px" }}>
          <span
            style={{
              width: 12,
              height: 12,
              flexShrink: 0,
              borderRadius: "50%",
              border: "2px solid #B0B7C3",
              background: "#FFFFFF",
            }}
          />
          <span style={{ flex: 1, fontSize: 10, color: VETIC_MUTED }}>Continue blocking</span>
        </div>
        {/* Done */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: CHROME_BLUE,
              padding: "4px 10px",
              borderRadius: 6,
              animation: `veticGuideDone ${GUIDE_LOOP} ease-in-out infinite`,
            }}
          >
            Done
          </span>
        </div>
      </div>

      {/* Animated cursor */}
      <div
        style={{
          position: "absolute",
          width: 16,
          height: 16,
          pointerEvents: "none",
          zIndex: 2,
          animation: `veticGuideCursor ${GUIDE_LOOP} ease-in-out infinite`,
          filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.3))",
        }}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="#1F1F23" stroke="#FFFFFF" strokeWidth="1">
          <path d="M4 2 L4 20 L9 15 L12 22 L15 21 L12 14 L19 14 Z" />
        </svg>
      </div>
    </div>
  );
};

type IncomingCallBlockedToastProps = {
  phoneNumber: string | null;
  visible: boolean;
  onOpen: () => void;
  onDismiss: () => void;
};

// Presentational card for the "popup blocked" incoming-call prompt. Kept purely
// visual — all open/redirect/dedupe logic lives in the caller (SocketProvider).
export const IncomingCallBlockedToast: React.FC<IncomingCallBlockedToastProps> = ({
  phoneNumber,
  visible,
  onOpen,
  onDismiss,
}) => {
  const displayNumber = phoneNumber ? phoneNumber : "Unknown number";

  return (
    <div
      style={{
        width: 360,
        maxWidth: "90vw",
        background: "#FFFFFF",
        color: VETIC_TEXT,
        borderRadius: 16,
        border: `1px solid ${VETIC_BORDER}`,
        boxShadow: "0 12px 32px rgba(25,58,106,0.18)",
        overflow: "hidden",
        fontFamily: "inherit",
        transform: visible ? "translateY(0)" : "translateY(-8px)",
        opacity: visible ? 1 : 0,
        transition: "all 0.2s ease",
      }}
    >
      <style>
        {`@keyframes veticCallPulse {
            0% { box-shadow: 0 0 0 0 rgba(25,58,106,0.35); }
            70% { box-shadow: 0 0 0 12px rgba(25,58,106,0); }
            100% { box-shadow: 0 0 0 0 rgba(25,58,106,0); }
          }`}
      </style>

      {/* Brand accent strip */}
      <div
        style={{
          height: 4,
          width: "100%",
          background: `linear-gradient(90deg, ${VETIC_NAVY} 0%, ${VETIC_ACCENT} 100%)`,
        }}
      />

      {/* Header: avatar + caller + close */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 16px 12px" }}>
        <div
          style={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: "50%",
            background: "rgba(25,58,106,0.1)",
            color: VETIC_NAVY,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            animation: "veticCallPulse 1.6s infinite",
          }}
        >
          📞
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: VETIC_NAVY,
            }}
          >
          CALL DETECTED
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1.3,
              color: VETIC_TEXT,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayNumber}
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            background: "transparent",
            color: VETIC_MUTED,
            border: "none",
            fontSize: 20,
            lineHeight: 1,
            cursor: "pointer",
            padding: 4,
            alignSelf: "flex-start",
          }}
        >
          ×
        </button>
      </div>

      {/* Blocked-popup hint */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          margin: "0 16px 10px",
          padding: "8px 10px",
          borderRadius: 10,
          background: "rgba(250,204,21,0.12)",
          border: "1px solid rgba(234,179,8,0.35)",
        }}
      >
        <span style={{ fontSize: 14, lineHeight: "18px" }}>⚠️</span>
        <span style={{ fontSize: 12, lineHeight: "18px", color: "#92400E" }}>
          Your browser blocked the call popup. Allow popups for this site so calls open
          automatically.
        </span>
      </div>

      {/* How-to-enable guide (always shown) */}
      <div style={{ margin: "0 16px 12px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: VETIC_NAVY,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <span style={{ fontSize: 13 }}>💡</span>
          How to enable pop-ups in Chrome
        </div>

        <div style={{ marginTop: 10 }}>
          <ChromePopupGuide />

          {/* Highlighted numbered steps */}
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              <>
                Click the <Hl>ⓘ “View site information”</Hl> icon at the left of the address bar.
              </>,
              <>
                Turn on the <Hl>“Pop-ups and redirects”</Hl> toggle.
              </>,
              <>
                <Hl>Reload</Hl> the page — future calls will now open automatically.
              </>,
            ].map((stepContent, index) => (
              <div key={index} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span
                  style={{
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    marginTop: 1,
                    borderRadius: "50%",
                    background: VETIC_NAVY,
                    color: "#FFFFFF",
                    fontSize: 10,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {index + 1}
                </span>
                <span style={{ fontSize: 11.5, lineHeight: "18px", color: VETIC_TEXT }}>
                  {stepContent}
                </span>
              </div>
            ))}
          </div>

          {/* Alternative callout */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              marginTop: 10,
              padding: "8px 10px",
              borderRadius: 10,
              background: "rgba(25,58,106,0.06)",
              border: "1px solid rgba(25,58,106,0.15)",
            }}
          >
            <span style={{ fontSize: 13, lineHeight: "18px" }}>💡</span>
            <span style={{ fontSize: 11, lineHeight: "17px", color: VETIC_TEXT }}>
              Alternatively, open <Hl>Site settings</Hl> and set{" "}
              <strong>Pop-ups and redirects</strong> to <Hl tone="green">Allow</Hl>.
            </span>
          </div>
        </div>
      </div>

      {/* Action */}
      <div style={{ padding: "0 16px 16px" }}>
        <button
          type="button"
          onClick={onOpen}
          style={{
            width: "100%",
            background: VETIC_NAVY,
            color: "#FFFFFF",
            border: "none",
            borderRadius: 10,
            padding: "11px 14px",
            fontWeight: 700,
            fontSize: 14,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            transition: "background 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = VETIC_NAVY_DARK;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = VETIC_NAVY;
          }}
        >
          Open Control Centre
        </button>
      </div>
    </div>
  );
};

// Shown ONLY when the browser's popup blocker suppressed the automatic call
// window. The automatic window.open() runs from a non-gesture context (a
// websocket frame behind a timeout + async lock), so a user who hasn't allowed
// popups for this site will have it blocked. This toast recovers that case: its
// "Open call" button click IS a genuine user gesture, so it opens the call
// reliably, and it tells the agent to allow popups so future calls open
// automatically with no prompt. Users who have allowed popups never see it.
export const showIncomingCallBlockedToast = (args: {
  eventKey: string;
  phoneNumber: string | null;
  onOpen: () => void;
  onDismiss?: () => void;
}) => {
  const { eventKey, phoneNumber, onOpen, onDismiss } = args;

  toast.custom(
    (t) => (
      <IncomingCallBlockedToast
        phoneNumber={phoneNumber}
        visible={t.visible}
        onOpen={() => {
          onOpen();
          toast.dismiss(t.id);
        }}
        onDismiss={() => {
          onDismiss?.();
          toast.dismiss(t.id);
        }}
      />
    ),
    {
      id: `exotel_incoming_call_${eventKey}`,
      duration: INCOMING_CALL_TOAST_DURATION_MS,
    },
  );
};
